import { useState } from 'react'
import { Download, Search } from 'lucide-react'
import { Button, Pagination } from '../../../../components'
import { AVATAR_COLOR_CLASSES } from '../constants/hrmsAttendanceLeave.constants'
import type { AttendanceRecord, DropdownOption } from '../types/hrmsAttendanceLeave.types'
import { AttendanceStatusPill } from './AttendanceStatusPill'

interface TodayAttendanceCardProps {
  records: AttendanceRecord[]
  total?: number
  page?: number
  limit?: number
  loading?: boolean
  search?: string
  onSearchChange?: (search: string) => void
  departmentId?: string
  onDepartmentChange?: (dept: string) => void
  departmentOptions?: DropdownOption[]
  status?: string
  onStatusChange?: (status: string) => void
  onPageChange?: (page: number) => void
  onViewAll: () => void
  onViewAttendance: (id: string) => void
  onExportDailySheet?: () => void
}

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: 'ALL' },
  { label: 'Present', value: 'PRESENT' },
  { label: 'Absent', value: 'ABSENT' },
  { label: 'Late', value: 'LATE' },
  { label: 'Half Day', value: 'HALF_DAY' },
  { label: 'On Leave', value: 'ON_LEAVE' },
]

function getInitials(name: string): string {
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function formatTime12h(timeStr?: string): string {
  if (!timeStr || timeStr === 'none' || timeStr === '—') return '—'
  const [hStr, mStr] = timeStr.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (isNaN(h) || isNaN(m)) return timeStr
  const period = h >= 12 ? 'PM' : 'AM'
  const displayH = h % 12 === 0 ? 12 : h % 12
  const formattedH = displayH < 10 ? `0${displayH}` : `${displayH}`
  const formattedM = m < 10 ? `0${m}` : `${m}`
  return `${formattedH}:${formattedM} ${period}`
}

function formatWorkingHours(minutes?: number): string {
  if (minutes === undefined || minutes === null || minutes === 0) return '—'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

export function TodayAttendanceCard({
  records,
  total,
  page,
  limit,
  loading = false,
  search = '',
  onSearchChange,
  departmentId = '',
  onDepartmentChange,
  departmentOptions = [],
  status = 'ALL',
  onStatusChange,
  onPageChange,
  onViewAll,
  onViewAttendance,
  onExportDailySheet,
}: TodayAttendanceCardProps) {
  // If controlled pagination is provided, use it; otherwise fallback to internal pagination
  const [localPage, setLocalPage] = useState<number>(1)
  const pageSize = limit || 5
  const isControlled = typeof page === 'number' && typeof total === 'number'

  const effectivePage = isControlled ? page : localPage
  const effectiveTotal = isControlled ? total : records.length
  const totalPages = Math.ceil(effectiveTotal / pageSize) || 1
  const safePage = Math.min(effectivePage, totalPages)

  const paginatedRecords = isControlled
    ? records
    : records.slice((safePage - 1) * pageSize, safePage * pageSize)

  const from = effectiveTotal === 0 ? 0 : (safePage - 1) * pageSize + 1
  const to = Math.min(safePage * pageSize, effectiveTotal)

  const handlePageSelect = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage)
    } else {
      setLocalPage(newPage)
    }
  }

  const handleExport = () => {
    if (onExportDailySheet) {
      onExportDailySheet()
      return
    }
    const headers = ['Employee', 'Employee ID', 'Department', 'Check In', 'Check Out', 'Working Hours', 'Status']
    const escapeCsv = (val: string) => `"${val.replace(/"/g, '""')}"`
    const rows = records.map((r) => [
      escapeCsv(r.name),
      escapeCsv(r.employeeId),
      escapeCsv(r.department),
      escapeCsv(formatTime12h(r.checkIn)),
      escapeCsv(formatTime12h(r.checkOut)),
      escapeCsv(formatWorkingHours(r.workingMinutes)),
      escapeCsv(r.status),
    ])
    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `Today_Attendance_Sheet.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xs min-w-0 space-y-4">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-slate-900">Today's Attendance</h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
              {effectiveTotal} {effectiveTotal === 1 ? 'employee' : 'employees'}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Quick view of today's employee attendance across primary campuses.
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0 self-start sm:self-center">
          <Button
            variant="secondary"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExport}
            className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 h-9 text-xs rounded-lg font-semibold shadow-xs whitespace-nowrap"
          >
            Export Daily Sheet
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={onViewAll}
            className="border-none bg-transparent hover:bg-slate-100 text-[#146971] p-0 font-semibold text-xs"
          >
            View All Attendance →
          </Button>
        </div>
      </div>

      {/* Filter Bar: Search, Department, Status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
        {/* Search */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search by name or employee ID..."
            className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-xs text-slate-800 placeholder-slate-400 focus:border-[#146971] focus:outline-none"
          />
        </div>

        {/* Department Filter */}
        {departmentOptions.length > 0 && (
          <div className="w-full">
            <select
              value={departmentId}
              onChange={(e) => onDepartmentChange?.(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 focus:border-[#146971] focus:outline-none cursor-pointer"
            >
              {departmentOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Status Filter */}
        <div className="w-full">
          <select
            value={status}
            onChange={(e) => onStatusChange?.(e.target.value)}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 focus:border-[#146971] focus:outline-none cursor-pointer"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto min-w-0 rounded-xl border border-slate-200">
        <table className="w-full min-w-[700px] text-left text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold border-b border-slate-200">
              <th className="py-3 px-4">EMPLOYEE</th>
              <th className="py-3 px-4">DEPARTMENT</th>
              <th className="py-3 px-4">CHECK IN</th>
              <th className="py-3 px-4">CHECK OUT</th>
              <th className="py-3 px-4">WORKING HOURS</th>
              <th className="py-3 px-4">STATUS</th>
              <th className="py-3 px-4 text-right">ACTION</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-slate-500">
                  <div className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#146971] border-t-transparent" />
                    <span>Loading today's attendance...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedRecords.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-slate-500">
                  No attendance records found for the selected filters
                </td>
              </tr>
            ) : (
              paginatedRecords.map((record, index) => {
                const colorClass = AVATAR_COLOR_CLASSES[index % AVATAR_COLOR_CLASSES.length]
                const isLate = String(record.status).toUpperCase() === 'LATE'

                return (
                  <tr key={record.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 ${colorClass}`}
                        >
                          {getInitials(record.name)}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{record.name}</div>
                          <div className="text-xs text-slate-500">{record.employeeId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium">{record.department}</td>
                    <td className={`py-3 px-4 font-medium ${isLate ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>
                      {formatTime12h(record.checkIn)}
                    </td>
                    <td className="py-3 px-4 text-slate-700 font-medium">
                      {formatTime12h(record.checkOut)}
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-900">
                      {formatWorkingHours(record.workingMinutes)}
                    </td>
                    <td className="py-3 px-4">
                      <AttendanceStatusPill status={record.status} lateMinutes={record.lateMinutes} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onViewAttendance(record.id)}
                        className="h-7 px-3 text-xs"
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pt-2">
          <Pagination
            summary={
              <span>
                Showing <span className="font-bold">{from}</span>–<span className="font-bold">{to}</span> of{' '}
                <span className="font-bold">{effectiveTotal}</span> records
              </span>
            }
            currentPage={safePage}
            totalPages={totalPages}
            previousDisabled={safePage === 1}
            nextDisabled={safePage === totalPages || totalPages === 0}
            onPrevious={() => handlePageSelect(Math.max(1, safePage - 1))}
            onNext={() => handlePageSelect(Math.min(totalPages, safePage + 1))}
            onPageChange={handlePageSelect}
          />
        </div>
      )}
    </div>
  )
}
